import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { teacherAPI } from '../../api/client'
import { useNavigate } from 'react-router-dom'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

type MainTab = 'classes' | 'submissions' | 'join'

/**
 * FR-017 — Teacher Dashboard
 * Shows: teacher's classes, student progress, assignments, submissions + comments
 * Also handles student "join class" flow
 */
export default function TeacherPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<MainTab>('classes')

  const NAV_ITEMS: { id: MainTab; icon: string; label: string }[] = [
    { id: 'classes', icon: 'school', label: 'Lớp của tôi' },
    { id: 'submissions', icon: 'rate_review', label: 'Bài nộp' },
    { id: 'join', icon: 'meeting_room', label: 'Tham gia lớp học' },
  ]

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", color: '#272e42' }}>
      {/* Sidebar */}
      <aside style={{ width: '17rem', flexShrink: 0, background: 'rgba(246,246,255,0.7)', backdropFilter: 'blur(24px)', display: 'flex', flexDirection: 'column', padding: '1.5rem', gap: '0.75rem', borderRight: '1px solid #eef0ff', position: 'sticky', top: 0, height: '100vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <div style={{ width: '3rem', height: '3rem', borderRadius: '0.875rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,75,226,0.3)' }}>
            <span className="material-symbols-outlined" style={{ color: 'white', fontVariationSettings: "'FILL' 1" }}>bolt</span>
          </div>
          <div>
            <H style={{ display: 'block', fontWeight: 900, fontSize: '1.125rem', color: '#004be2', lineHeight: 1 }}>Tutor Portal</H>
            <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#a5adc6' }}>Precision Learning</span>
          </div>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderRadius: '0.875rem', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'all 0.2s',
                background: tab === item.id ? '#004be2' : 'transparent',
                color: tab === item.id ? 'white' : '#535b71',
                boxShadow: tab === item.id ? '0 8px 20px rgba(0,75,226,0.3)' : 'none',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: tab === item.id ? "'FILL' 1" : "'FILL' 0" }}>{item.icon}</span>
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{item.label}</span>
            </button>
          ))}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button onClick={() => navigate('/dashboard')} style={{ width: '100%', padding: '0.875rem', borderRadius: '0.875rem', border: 'none', background: '#d1dcff', color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem' }}>
            Về Dashboard
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '2.5rem', minWidth: 0 }}>
        {tab === 'classes' && <TeacherClassesPanel />}
        {tab === 'submissions' && <SubmissionsPanel />}
        {tab === 'join' && <JoinClassPanel />}
      </main>

      {/* FAB */}
      <button
        onClick={() => setTab('classes')}
        style={{ position: 'fixed', bottom: '2rem', right: '2rem', width: '3.5rem', height: '3.5rem', borderRadius: '50%', background: 'linear-gradient(135deg,#004be2,#809bff)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 40px rgba(0,75,226,0.3)', zIndex: 50 }}
      >
        <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.5rem' }}>add</span>
      </button>
    </div>
  )
}

// ─── Teacher Classes Panel ────────────────────────────────────────────────────

function TeacherClassesPanel() {
  const qc = useQueryClient()
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newClassName, setNewClassName] = useState('')
  const [newClassDesc, setNewClassDesc] = useState('')

  const { data: classes, isLoading } = useQuery({
    queryKey: ['teacher-classes'],
    queryFn: teacherAPI.getClasses,
  })

  const createClass = useMutation({
    mutationFn: () => teacherAPI.createClass({ name: newClassName, description: newClassDesc }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teacher-classes'] })
      setShowCreate(false)
      setNewClassName('')
      setNewClassDesc('')
    },
  })

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div className="spinner" />
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>school</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Lớp học</span>
          </div>
          <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
            Lớp <span style={{ color: '#004be2', fontStyle: 'italic' }}>của tôi</span>
          </H>
          <p style={{ color: '#535b71', fontSize: '1rem' }}>Quản lý và theo dõi học viên trong các lớp học của bạn.</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          style={{ padding: '0.875rem 1.5rem', borderRadius: '9999px', border: 'none', background: showCreate ? '#eef0ff' : 'linear-gradient(135deg,#004be2,#809bff)', color: showCreate ? '#535b71' : 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: showCreate ? 'none' : '0 8px 24px rgba(0,75,226,0.25)' }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>{showCreate ? 'close' : 'add'}</span>
          {showCreate ? 'Hủy' : 'Tạo lớp mới'}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', marginBottom: '1.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.06)' }}>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem', marginBottom: '1.25rem' }}>Tạo lớp học mới</H>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Tên lớp *</label>
              <input
                value={newClassName}
                onChange={e => setNewClassName(e.target.value)}
                placeholder="VD: VSTEP B2 — Khóa Hè 2026"
                style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Mô tả</label>
              <input
                value={newClassDesc}
                onChange={e => setNewClassDesc(e.target.value)}
                placeholder="Mô tả ngắn về lớp học..."
                style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowCreate(false)} style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>Hủy</button>
            <button
              onClick={() => createClass.mutate()}
              disabled={!newClassName || createClass.isPending}
              style={{ padding: '0.75rem 2rem', borderRadius: '9999px', border: 'none', background: newClassName ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d1dcff', color: newClassName ? 'white' : '#a5adc6', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
            >
              {createClass.isPending ? 'Đang tạo...' : 'Tạo lớp'}
            </button>
          </div>
          {createClass.isError && (
            <p style={{ color: '#b41340', fontSize: '0.875rem', marginTop: '0.75rem' }}>{(createClass.error as Error).message}</p>
          )}
        </div>
      )}

      {/* Invite code banner */}
      {createClass.isSuccess && createClass.data && (
        <div style={{ background: 'linear-gradient(135deg,#e8eeff,#d1dcff)', borderRadius: '1.25rem', padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.5rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
          <div>
            <p style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Lớp đã tạo! Mã mời học sinh:</p>
            <H style={{ fontWeight: 900, fontSize: '1.5rem', color: '#004be2', letterSpacing: '0.1em' }}>{createClass.data.inviteCode}</H>
            <p style={{ fontSize: '0.8125rem', color: '#535b71', marginTop: '0.25rem' }}>Chia sẻ mã này cho học sinh để họ tham gia lớp.</p>
          </div>
        </div>
      )}

      {/* Class list */}
      {!classes || classes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>school</span>
          <H style={{ display: 'block', fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.5rem', color: '#535b71' }}>Chưa có lớp nào</H>
          <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Tạo lớp đầu tiên để bắt đầu quản lý học viên.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {classes.map((cls: any) => (
            <div key={cls.id} style={{ background: 'white', borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <div
                onClick={() => setSelectedClassId(selectedClassId === cls.id ? null : cls.id)}
                style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              >
                <div>
                  <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem', marginBottom: '0.25rem' }}>{cls.name}</H>
                  {cls.description && <p style={{ color: '#535b71', fontSize: '0.875rem' }}>{cls.description}</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ display: 'block', fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a5adc6', marginBottom: '0.25rem' }}>Mã mời</span>
                    <H style={{ fontWeight: 900, color: '#004be2', letterSpacing: '0.1em' }}>{cls.inviteCode}</H>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ display: 'block', fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a5adc6', marginBottom: '0.25rem' }}>Học sinh</span>
                    <H style={{ fontWeight: 900 }}>{cls.memberCount}</H>
                  </div>
                  <span className="material-symbols-outlined" style={{ color: '#a5adc6', transition: 'transform 0.2s', transform: selectedClassId === cls.id ? 'rotate(180deg)' : 'none' }}>expand_more</span>
                </div>
              </div>
              {selectedClassId === cls.id && (
                <div style={{ borderTop: '1px solid #eef0ff' }}>
                  <ClassDetail classId={cls.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Class Detail — students + assignments ────────────────────────────────────

function ClassDetail({ classId }: { classId: string }) {
  const qc = useQueryClient()
  const [detailTab, setDetailTab] = useState<'students' | 'assignments'>('students')
  const [showAssignForm, setShowAssignForm] = useState(false)
  const [assignForm, setAssignForm] = useState({
    title: '', description: '', skill: '', targetCount: 1, dueDate: '',
  })

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['class-students', classId],
    queryFn: () => teacherAPI.getStudents(classId),
  })

  const { data: assignments, isLoading: assignLoading } = useQuery({
    queryKey: ['class-assignments', classId],
    queryFn: () => teacherAPI.getAssignments(classId),
  })

  const createAssignment = useMutation({
    mutationFn: () => teacherAPI.createAssignment({
      classId,
      title: assignForm.title,
      description: assignForm.description || undefined,
      skill: (assignForm.skill || undefined) as any,
      targetCount: assignForm.targetCount,
      dueDate: assignForm.dueDate || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['class-assignments', classId] })
      setShowAssignForm(false)
      setAssignForm({ title: '', description: '', skill: '', targetCount: 1, dueDate: '' })
    },
  })

  const inputStyle: React.CSSProperties = { width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxSizing: 'border-box' }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Detail tabs */}
      <div style={{ display: 'flex', background: '#eef0ff', borderRadius: '0.875rem', padding: '0.25rem', marginBottom: '1.25rem', width: 'fit-content' }}>
        {([
          { id: 'students', label: `Học sinh (${students?.length ?? 0})` },
          { id: 'assignments', label: `Bài tập (${assignments?.length ?? 0})` },
        ] as const).map(t => (
          <button
            key={t.id}
            onClick={() => setDetailTab(t.id)}
            style={{ padding: '0.5rem 1.25rem', borderRadius: '0.625rem', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.8125rem', fontFamily: "'Space Grotesk', sans-serif", background: detailTab === t.id ? 'white' : 'transparent', color: detailTab === t.id ? '#004be2' : '#a5adc6', boxShadow: detailTab === t.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}
          >{t.label}</button>
        ))}
      </div>

      {detailTab === 'students' && (
        <div>
          {studentsLoading ? <div style={{ padding: '2rem', textAlign: 'center', color: '#a5adc6' }}>Đang tải...</div> : (
            !students || students.length === 0 ? (
              <p style={{ color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Chưa có học sinh nào. Chia sẻ mã mời để học sinh tham gia.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 0.5rem' }}>
                  <thead>
                    <tr>
                      {['Tên', 'Mục tiêu', 'XP', 'Streak', 'Nghe', 'Đọc', 'Viết'].map(h => (
                        <th key={h} style={{ padding: '0.5rem 1rem', textAlign: 'left', fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a5adc6' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {students?.map((s: any) => (
                      <tr key={s.id}>
                        {[
                          s.name,
                          s.targetLevel ?? '—',
                          s.totalXp,
                          `${s.streak}`,
                          s.currentScores.listening?.toFixed(1) ?? '—',
                          s.currentScores.reading?.toFixed(1) ?? '—',
                          s.currentScores.writing?.toFixed(1) ?? '—',
                        ].map((val, i) => (
                          <td key={i} style={{ padding: '0.875rem 1rem', background: 'white', borderRadius: i === 0 ? '0.875rem 0 0 0.875rem' : i === 6 ? '0 0.875rem 0.875rem 0' : 0, fontSize: '0.875rem', fontWeight: i === 0 ? 700 : 500, color: '#272e42' }}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {detailTab === 'assignments' && (
        <div>
          <button
            onClick={() => setShowAssignForm(!showAssignForm)}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none', background: showAssignForm ? '#eef0ff' : 'linear-gradient(135deg,#004be2,#809bff)', color: showAssignForm ? '#535b71' : 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', marginBottom: '1.25rem', fontSize: '0.875rem' }}
          >
            {showAssignForm ? 'Hủy' : '+ Tạo bài tập'}
          </button>

          {showAssignForm && (
            <div style={{ background: '#eef0ff', borderRadius: '1.25rem', padding: '1.5rem', marginBottom: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '0.875rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Tên bài tập *</label>
                  <input value={assignForm.title} onChange={e => setAssignForm({ ...assignForm, title: e.target.value })} placeholder="Luyện Listening Part 3" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Kỹ năng</label>
                  <select value={assignForm.skill} onChange={e => setAssignForm({ ...assignForm, skill: e.target.value })} style={{ ...inputStyle }}>
                    <option value="">Tất cả</option>
                    {['listening', 'reading', 'writing', 'speaking', 'vocabulary'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Số lượng cần hoàn thành</label>
                  <input type="number" min={1} max={100} value={assignForm.targetCount} onChange={e => setAssignForm({ ...assignForm, targetCount: Number(e.target.value) })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Hạn nộp</label>
                  <input type="date" value={assignForm.dueDate} onChange={e => setAssignForm({ ...assignForm, dueDate: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Mô tả</label>
                <textarea value={assignForm.description} onChange={e => setAssignForm({ ...assignForm, description: e.target.value })} rows={2} placeholder="Hướng dẫn thêm cho học sinh..." style={{ ...inputStyle, resize: 'none' }} />
              </div>
              <button
                onClick={() => createAssignment.mutate()}
                disabled={!assignForm.title || createAssignment.isPending}
                style={{ padding: '0.75rem 2rem', borderRadius: '9999px', border: 'none', background: assignForm.title ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d1dcff', color: assignForm.title ? 'white' : '#a5adc6', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
              >
                {createAssignment.isPending ? 'Đang tạo...' : 'Tạo bài tập'}
              </button>
            </div>
          )}

          {assignLoading ? <div style={{ padding: '2rem', textAlign: 'center', color: '#a5adc6' }}>Đang tải...</div> : (
            !assignments || assignments.length === 0 ? (
              <p style={{ color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Chưa có bài tập nào.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {assignments?.map((a: any) => (
                  <div key={a.id} style={{ background: 'white', borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <H style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{a.title}</H>
                      {a.skill && (
                        <span style={{ padding: '0.2rem 0.625rem', borderRadius: '9999px', fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', background: '#eef0ff', color: '#004be2' }}>{a.skill}</span>
                      )}
                    </div>
                    {a.description && <p style={{ color: '#535b71', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{a.description}</p>}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', fontWeight: 700, color: '#a5adc6' }}>
                      <span>Mục tiêu: {a.targetCount} lần</span>
                      {a.dueDate && <span>Hạn: {new Date(a.dueDate).toLocaleDateString('vi')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

// ─── Submissions Panel — D-4 Teacher Comments ────────────────────────────────

function SubmissionsPanel() {
  const qc = useQueryClient()
  const [subType, setSubType] = useState<'writing' | 'speaking'>('writing')
  const [selectedSub, setSelectedSub] = useState<any | null>(null)
  const [commentText, setCommentText] = useState('')
  const [scoreOverride, setScoreOverride] = useState('')

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['teacher-submissions', subType],
    queryFn: () => teacherAPI.getSubmissions(subType),
  })

  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ['teacher-comments', subType, selectedSub?.id],
    queryFn: () => teacherAPI.getComments(subType, selectedSub!.id),
    enabled: !!selectedSub,
  })

  const addComment = useMutation({
    mutationFn: () => teacherAPI.addComment({
      submissionType: subType,
      submissionId: selectedSub!.id,
      studentId: selectedSub!.userId,
      comment: commentText,
      scoreOverride: scoreOverride ? Number(scoreOverride) : undefined,
    }),
    onSuccess: () => {
      setCommentText('')
      setScoreOverride('')
      qc.invalidateQueries({ queryKey: ['teacher-comments', subType, selectedSub?.id] })
      qc.invalidateQueries({ queryKey: ['teacher-submissions', subType] })
    },
  })

  const deleteComment = useMutation({
    mutationFn: (id: string) => teacherAPI.deleteComment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teacher-comments', subType, selectedSub?.id] }),
  })

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem',
    background: '#eef0ff', border: 'none', outline: 'none',
    fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', boxSizing: 'border-box',
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>rate_review</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Nhận xét</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Bài nộp <span style={{ color: '#004be2', fontStyle: 'italic' }}>của học sinh</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem' }}>Xem và nhận xét bài Writing/Speaking của học sinh trong lớp bạn.</p>
      </div>

      {/* Type toggle */}
      <div style={{ display: 'flex', background: '#eef0ff', borderRadius: '0.875rem', padding: '0.25rem', marginBottom: '1.5rem', width: 'fit-content' }}>
        {(['writing', 'speaking'] as const).map(t => (
          <button
            key={t}
            onClick={() => { setSubType(t); setSelectedSub(null) }}
            style={{
              padding: '0.625rem 1.5rem', borderRadius: '0.625rem', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.875rem', fontFamily: "'Space Grotesk', sans-serif",
              background: subType === t ? 'white' : 'transparent',
              color: subType === t ? '#004be2' : '#a5adc6',
              boxShadow: subType === t ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: subType === t ? "'FILL' 1" : "'FILL' 0" }}>
              {t === 'writing' ? 'edit_note' : 'mic'}
            </span>
            {t === 'writing' ? 'Writing' : 'Speaking'}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedSub ? '1fr 1.5fr' : '1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Submissions list */}
        <div>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="spinner" /></div>
          ) : !submissions || submissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>inbox</span>
              <H style={{ display: 'block', fontWeight: 700, color: '#535b71', marginBottom: '0.5rem' }}>Chưa có bài nộp</H>
              <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Học sinh trong lớp bạn chưa nộp bài {subType}.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {submissions.map((sub: any) => {
                const isSelected = selectedSub?.id === sub.id
                return (
                  <div
                    key={sub.id}
                    onClick={() => setSelectedSub(isSelected ? null : sub)}
                    style={{
                      background: 'white', borderRadius: '1.25rem', padding: '1.25rem',
                      cursor: 'pointer', transition: 'all 0.2s',
                      border: `2px solid ${isSelected ? '#004be2' : 'transparent'}`,
                      boxShadow: isSelected ? '0 8px 24px rgba(0,75,226,0.12)' : '0 4px 12px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div>
                        <H style={{ display: 'block', fontWeight: 700, fontSize: '0.9375rem', marginBottom: '0.25rem' }}>{sub.userName}</H>
                        <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>
                          {subType === 'writing'
                            ? `${sub.taskType?.toUpperCase()} · ${sub.wordCount ?? 0} từ`
                            : `${sub.partType?.toUpperCase()} · ${sub.audioDuration ?? 0}s`}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {sub.scoreOverall != null && (
                          <H style={{ display: 'block', fontWeight: 900, fontSize: '1.25rem', color: '#004be2' }}>
                            {Number(sub.scoreOverall).toFixed(1)}
                          </H>
                        )}
                        <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}>
                          {new Date(sub.submittedAt).toLocaleDateString('vi-VN')}
                        </span>
                      </div>
                    </div>
                    {sub.taskPrompt && (
                      <p style={{ fontSize: '0.8125rem', color: '#535b71', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sub.taskPrompt}
                      </p>
                    )}
                    {sub.commentCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.5rem', fontSize: '0.75rem', color: '#004be2', fontWeight: 700 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>comment</span>
                        {sub.commentCount} nhận xét
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Comment panel */}
        {selectedSub && (
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', position: 'sticky', top: '1.5rem' }}>
            {/* Submission detail */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem', marginBottom: '0.25rem' }}>{selectedSub.userName}</H>
                <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>
                  {subType === 'writing'
                    ? `${selectedSub.taskType?.toUpperCase()} · ${selectedSub.wordCount ?? 0} từ · Điểm AI: ${selectedSub.scoreOverall?.toFixed(1) ?? '—'}`
                    : `${selectedSub.partType?.toUpperCase()} · Điểm AI: ${selectedSub.scoreOverall?.toFixed(1) ?? '—'}`}
                </span>
              </div>
              <button
                onClick={() => setSelectedSub(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6', padding: '0.25rem' }}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* Task prompt */}
            {selectedSub.taskPrompt && (
              <div style={{ background: '#f6f6ff', borderRadius: '0.875rem', padding: '1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#535b71', lineHeight: 1.6 }}>
                <strong>Đề bài:</strong> {selectedSub.taskPrompt}
              </div>
            )}

            {/* Existing comments */}
            <div style={{ marginBottom: '1.25rem' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem', marginBottom: '0.875rem' }}>
                Nhận xét của giáo viên
              </H>
              {commentsLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#a5adc6' }}>Đang tải...</div>
              ) : !comments || comments.length === 0 ? (
                <p style={{ color: '#a5adc6', fontSize: '0.875rem', fontStyle: 'italic' }}>Chưa có nhận xét nào.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '16rem', overflowY: 'auto' }}>
                  {(comments as any[]).map((c: any) => (
                    <div key={c.id} style={{ background: '#eef0ff', borderRadius: '1rem', padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#004be2' }}>{c.teacherName}</span>
                          {c.scoreOverride != null && (
                            <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', background: '#004be2', color: 'white', fontSize: '0.6875rem', fontWeight: 800 }}>
                              Điểm: {Number(c.scoreOverride).toFixed(1)}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}>
                            {new Date(c.createdAt).toLocaleDateString('vi-VN')}
                          </span>
                          <button
                            onClick={() => { if (confirm('Xóa nhận xét này?')) deleteComment.mutate(c.id) }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b41340', padding: '0.125rem' }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>delete</span>
                          </button>
                        </div>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#272e42', lineHeight: 1.6, margin: 0 }}>{c.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add comment form */}
            <div style={{ borderTop: '1px solid #eef0ff', paddingTop: '1.25rem' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem', marginBottom: '0.875rem' }}>Thêm nhận xét</H>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Nhận xét của bạn về bài này..."
                rows={4}
                style={{ ...inputStyle, resize: 'none', marginBottom: '0.75rem' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.375rem' }}>
                    Điểm override (0–10, tùy chọn)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    step={0.5}
                    value={scoreOverride}
                    onChange={e => setScoreOverride(e.target.value)}
                    placeholder="VD: 7.5"
                    style={{ ...inputStyle }}
                  />
                </div>
              </div>
              <button
                onClick={() => addComment.mutate()}
                disabled={!commentText.trim() || addComment.isPending}
                style={{
                  width: '100%', padding: '0.875rem', borderRadius: '9999px', border: 'none',
                  background: commentText.trim() ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d1dcff',
                  color: commentText.trim() ? 'white' : '#a5adc6',
                  fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                }}
              >
                {addComment.isPending
                  ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang lưu...</>
                  : <><span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>send</span> Gửi nhận xét</>
                }
              </button>
              {addComment.isError && (
                <p style={{ color: '#b41340', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                  {(addComment.error as Error).message}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Join Class Panel (for students) ─────────────────────────────────────────

function JoinClassPanel() {
  const qc = useQueryClient()
  const [inviteCode, setInviteCode] = useState('')

  const { data: myClasses, isLoading } = useQuery({
    queryKey: ['my-classes'],
    queryFn: teacherAPI.getMyClasses,
  })

  const joinClass = useMutation({
    mutationFn: () => teacherAPI.joinClass(inviteCode.trim().toUpperCase()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-classes'] })
      setInviteCode('')
    },
  })

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>meeting_room</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Học viên</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Tham gia <span style={{ color: '#004be2', fontStyle: 'italic' }}>lớp học</span>
        </H>
      </div>

      {/* Join form */}
      <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', marginBottom: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
        <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem', marginBottom: '1.25rem' }}>Tham gia bằng mã mời</H>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={inviteCode}
            onChange={e => setInviteCode(e.target.value.toUpperCase())}
            placeholder="Nhập mã 6 ký tự..."
            maxLength={6}
            style={{ flex: 1, minWidth: '12rem', padding: '0.875rem 1.25rem', borderRadius: '9999px', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '1.125rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#272e42' }}
          />
          <button
            disabled={inviteCode.length !== 6 || joinClass.isPending}
            onClick={() => joinClass.mutate()}
            style={{ padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none', background: inviteCode.length === 6 ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d1dcff', color: inviteCode.length === 6 ? 'white' : '#a5adc6', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: inviteCode.length === 6 ? 'pointer' : 'not-allowed', boxShadow: inviteCode.length === 6 ? '0 8px 20px rgba(0,75,226,0.25)' : 'none' }}
          >
            {joinClass.isPending ? 'Đang vào...' : 'Tham gia →'}
          </button>
        </div>
        {joinClass.isError && <p style={{ color: '#b41340', fontSize: '0.875rem', marginTop: '0.875rem' }}>{(joinClass.error as Error).message}</p>}
        {joinClass.isSuccess && joinClass.data && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginTop: '0.875rem', color: '#006571', fontWeight: 700, fontSize: '0.875rem' }}>
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1", fontSize: '1.125rem' }}>check_circle</span>
            Đã tham gia lớp <strong>{joinClass.data.className}</strong>
          </div>
        )}
      </div>

      {/* My classes */}
      <H style={{ display: 'block', fontWeight: 800, fontSize: '1.125rem', marginBottom: '1rem' }}>Lớp học đang tham gia</H>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" /></div>
      ) : !myClasses || myClasses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>meeting_room</span>
          <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Bạn chưa tham gia lớp học nào. Nhập mã mời từ giáo viên.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(18rem, 1fr))', gap: '1rem' }}>
          {myClasses?.map((cls: any) => (
            <div key={cls.id} style={{ background: 'white', borderRadius: '1.25rem', padding: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '0.375rem' }}>{cls.name}</H>
              {cls.description && <p style={{ color: '#535b71', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>{cls.description}</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '0.8125rem', color: '#a5adc6', fontWeight: 600 }}>
                <span>Giáo viên: {cls.teacherName}</span>
                <span>Tham gia: {new Date(cls.joinedAt).toLocaleDateString('vi')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
